import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { BuildPriceEntities } from 'client/data/models/build-price';
import { getUnifiedLeadFormDealerCountV2Path, UnifiedLeadFormModel } from 'client/data/models/unified-lead-form';
import { buildNoSubmodelVehiclePath, VehicleModel } from 'client/data/models/vehicle-v2';
import { STEP_NUMBER } from 'site-modules/shared/constants/build-price';
import { objectToQueryString } from 'site-modules/shared/utils/string';
import { LEAD_FORM_CONFIGS } from 'site-modules/shared/constants/lead-form/lead-form-configs';

import './build-steps-menu.scss';

const STEPS_TO_MENU_MAP = [
  [STEP_NUMBER.STEP_1, STEP_NUMBER.STEP_2, STEP_NUMBER.STEP_2_5],
  [STEP_NUMBER.STEP_3],
  [STEP_NUMBER.STEP_4],
  [STEP_NUMBER.STEP_5],
];

function BuildStepsMenuUI({
  buildPriceFilter: { step, styleFilter, styleId, optionIds, urlOptionIds },
  mostPopularStyle = null,
  leadFormDealerCount = null,
  isMobile = false,
  pathname,
}) {
  const { modelyearId } = styleFilter;
  const hasLeadForm = !!((mostPopularStyle || styleId) && leadFormDealerCount);
  const styleQuery = styleId
    ? { ...styleFilter, styleId, optionIds: urlOptionIds || optionIds }
    : {
        modelyearId: get(mostPopularStyle, 'modelYearId'),
        submodel: get(mostPopularStyle, 'submodelIdentifier'),
        styleId: get(mostPopularStyle, 'id'),
      };

  const menuItems = [
    {
      text: 'Choose Trim',
      query: { defaultValue: modelyearId },
      trackingId: 'build_price_step_choose_vehicle_type',
    },
    {
      text: 'Add Options',
      query: styleQuery,
      trackingId: 'build_price_step_add_options',
    },
    {
      text: 'Calculate Price',
      query: { ...styleQuery, priceBreakdown: '' },
      trackingId: 'build_price_step_see_pricing',
    },
  ];

  if (hasLeadForm) {
    menuItems.push({
      text: 'Get Quote',
      query: { ...styleQuery, priceBreakdown: '', getQuotes: '' },
      trackingId: 'build_price_step_get_quote',
    });
  }

  return (
    <Row className="mt-1_5 mt-md-2">
      <Col xs={12} lg={hasLeadForm ? 7 : 6}>
        <nav className="build-steps-menu" aria-label="menu build and price">
          <ol
            className={classnames(
              'menu-list list-unstyled d-flex flex-nowrap justify-content-between mb-0 text-gray-darker',
              {
                'no-quotes': !hasLeadForm,
              }
            )}
          >
            {menuItems.map(({ text, query, trackingId }, index) => {
              const isActive = index === STEPS_TO_MENU_MAP.findIndex(steps => steps.includes(step));
              const hasRequiredQuery = !!(query.defaultValue || query.styleId);
              const isLink = !(isActive || !hasRequiredQuery);
              const ItemTag = isLink ? 'a' : 'div';
              const itemProps = isLink
                ? {
                    href: `${pathname}?${objectToQueryString(query)}`,
                    'data-tracking-id': trackingId,
                    'data-tracking-value': text,
                  }
                : {};

              return (
                <li
                  key={text}
                  className={classnames('fw-bold d-inline-block text-center mb-0', {
                    'active text-primary-darker': isActive,
                    small: isMobile,
                  })}
                >
                  <ItemTag
                    {...itemProps}
                    className="item text-decoration-none d-block pb-0_5 px-0_25 px-md-0_75"
                    aria-current={isActive ? 'step' : undefined}
                  >
                    <div
                      className={classnames('item-number mx-auto mb-0_5', {
                        'bg-primary-darker text-white': isActive,
                        'bg-gray-lighter': !isActive,
                        medium: !isMobile,
                        small: isMobile,
                      })}
                    >
                      {index + 1}
                    </div>
                    {text}
                  </ItemTag>
                </li>
              );
            })}
          </ol>
        </nav>
      </Col>
    </Row>
  );
}

BuildStepsMenuUI.propTypes = {
  buildPriceFilter: BuildPriceEntities.BuildPriceFilter.isRequired,
  pathname: PropTypes.string.isRequired,
  mostPopularStyle: PropTypes.shape({
    id: PropTypes.number,
    modelYearId: PropTypes.number,
    submodelIdentifier: PropTypes.string,
  }),
  leadFormDealerCount: PropTypes.number,
  isMobile: PropTypes.bool,
};

const stateToPropsConfig = {
  mostPopularStyle: bindToPath(
    ({ makeSlug, modelSlug, year, buildPriceFilter: { styleId } }) =>
      !styleId && buildNoSubmodelVehiclePath({ make: makeSlug, model: modelSlug, year }),
    VehicleModel,
    ({ modelYearId, mostPopularStyleId, mostPopularSubmodel }) => ({
      id: mostPopularStyleId,
      modelYearId,
      submodelIdentifier: get(mostPopularSubmodel, 'identifier'),
    })
  ),
  leadFormDealerCount: bindToPath(
    ({ makeSlug, modelSlug, isSummaryLeadFormSubmitted }) =>
      !isSummaryLeadFormSubmitted &&
      getUnifiedLeadFormDealerCountV2Path({
        makeSlug,
        modelSlug,
        strategy: LEAD_FORM_CONFIGS.QUICK_QUOTES_TRIM_DST.strategy,
      }),
    UnifiedLeadFormModel
  ),
};

export const BuildStepsMenu = connectToModel(BuildStepsMenuUI, stateToPropsConfig);
